/**
 * Environment Globals
 *
 * Lightweight module for environment singletons stored on globalThis.
 * No imports from env modules to avoid circular dependencies.
 *
 * Provides:
 * - Global key constants (single source of truth for names)
 * - Type-safe get/set/clear accessors
 * - Reset functions for test isolation
 *
 * Note: TypeScript type declarations for globals are in each env module
 * (server.ts, client.ts, payload.ts) to keep types colocated with their definitions.
 */

// =============================================================================
// GLOBAL KEY CONSTANTS
// =============================================================================

export const KEYS = {
    SERVER_ENV: '__SERVER_ENV__',
    SERVER_CONFIG: '__SERVER_CONFIG__',
    CLIENT_ENV: '__CLIENT_ENV__',
    CLIENT_CONFIG: '__CLIENT_CONFIG__',
    PAYLOAD_ENV: '__PAYLOAD_ENV__',
    /** The C2C servability lookup + its connection pool. On globalThis because the
     *  middleware is a separate bundle, so a module-level pool would not be the one the
     *  boot path warmed — see c2c-servability.ts. */
    C2C_SERVABILITY: '__C2C_SERVABILITY__',
} as const

export type GlobalKey = (typeof KEYS)[keyof typeof KEYS]

// =============================================================================
// ACCESSORS
// =============================================================================

const globals = globalThis as Record<string, unknown>

/**
 * Get a value from globalThis.
 * @example getGlobal<ServerEnv>(KEYS.SERVER_ENV)
 */
export function getGlobal<T>(key: GlobalKey): T | undefined {
    return globals[key] as T | undefined
}

/**
 * Set a value on globalThis.
 * @example setGlobal(KEYS.SERVER_ENV, env)
 */
export function setGlobal<T>(key: GlobalKey, value: T): void {
    globals[key] = value
}

/**
 * Clear a value from globalThis.
 * @example clearGlobal(KEYS.SERVER_ENV)
 */
export function clearGlobal(key: GlobalKey): void {
    globals[key] = undefined
}

// =============================================================================
// RESET FUNCTIONS (for test isolation)
// =============================================================================

/**
 * Resets the ServerEnv singleton and config.
 */
export function resetServerEnv(): void {
    clearGlobal(KEYS.SERVER_ENV)
    clearGlobal(KEYS.SERVER_CONFIG)
}

/**
 * Resets the ClientEnv singleton and config.
 */
export function resetClientEnv(): void {
    clearGlobal(KEYS.CLIENT_ENV)
    clearGlobal(KEYS.CLIENT_CONFIG)
}

/**
 * Resets the PayloadEnv singleton.
 */
export function resetPayloadEnv(): void {
    clearGlobal(KEYS.PAYLOAD_ENV)
}

/**
 * Resets the cached C2C servability lookup.
 *
 * Separate from the env resets because it is not an env singleton: it is process state the
 * boot path warms and the middleware reads. It still needs clearing between tests, since a
 * pool left behind by one test silently answers another test's routing decision.
 */
export function resetC2CServability(): void {
    // Close the connection pool before dropping the reference — clearing the slot alone
    // orphans it, and a suite that resets between cases would leak one per case.
    const wired = getGlobal<{ close?: () => Promise<void> }>(KEYS.C2C_SERVABILITY)
    void wired?.close?.().catch(() => undefined)
    clearGlobal(KEYS.C2C_SERVABILITY)
}

/**
 * Resets all environment singletons, and releases the non-environment process state held
 * under the same global keys (the C2C servability lookup, whose connection pool must be
 * closed rather than merely dereferenced).
 *
 * Convenience function for test setup.
 */
export function resetAllEnvs(): void {
    resetServerEnv()
    resetClientEnv()
    resetPayloadEnv()
    resetC2CServability()
}
